import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { Plugin } from '@nuxt/types';
import { getCookie } from '../utils/cookie';

const VALID_AUDIENCE_FILTER_ENGINES = ['legacy', 'filter2', 'shadow'];

function hasEnabledFeature(features: any[] | undefined, featureId: string): boolean {
  return Array.isArray(features) && features.some(feature => feature.id === featureId && feature.enabled);
}

function getAudienceFilterEngine(features: any[] | undefined): string {
  if (hasEnabledFeature(features, 'filter2')) {
    return 'filter2';
  }

  if (hasEnabledFeature(features, 'shadow')) {
    return 'shadow';
  }

  if (process.env.NODE_ENV === 'production') {
    return 'legacy';
  }

  const envFilterEngine = process.env.arAudienceFilterEngine;
  if (envFilterEngine && VALID_AUDIENCE_FILTER_ENGINES.includes(envFilterEngine)) {
    return envFilterEngine;
  }

  return 'legacy';
}

function isFanFilterRequest(url?: string): boolean {
  return !!url && /\/filter-fan(?:-export)?\/?(\?|$)/.test(url);
}

export function applyAudienceFilterEngine(config: any, features: any[] | undefined) {
  if (!isFanFilterRequest(config.url)) {
    return;
  }

  config.params = config.params || {};
  delete config.params.$filterEngine;
  config.params.filterEngine = getAudienceFilterEngine(features);
}

// Override NuxtAxiosInstance
declare module '@nuxtjs/axios' {
  // XXX WHY THE HELL IS THIS OVERRIDING A LIBRARY TYPE?!
  //  it completely changes its signature
  interface NuxtAxiosInstance {
    sg: AxiosInstance;
    cn: AxiosInstance;
    mono: AxiosInstance;
  }
}

// Create a global middleware completion tracker
let middlewareComplete = false;
const middlewarePromise = new Promise<void>((resolve) => {
  // Expose a global resolver that can be called when middleware completes
  if (typeof window !== 'undefined') {
    (window as any).__resolveMiddlewarePromise = () => {
      middlewareComplete = true;
      resolve();
    };
  }
});

// Helper function to ensure middleware is complete
async function ensureMiddlewareComplete () {
  if (!middlewareComplete) {
    try {
      // Wait with a timeout to prevent hanging if there's an issue
      const timeoutPromise = new Promise<void>((resolve) =>
        setTimeout(() => {
          console.warn('Middleware completion timeout - proceeding with request');
          resolve();
        }, 2000)
      );

      await Promise.race([middlewarePromise, timeoutPromise]);
    } catch (e) {
      console.error('Error waiting for middleware:', e);
    }
  }
}

const plugin: Plugin = function({ $axios, store }) {
  $axios.onRequest((config) => {
    applyAudienceFilterEngine(config, store.state.auth?.account?.features);

    const token = getCookie('x-auth-token') || window.sessionStorage.getItem('x-auth-token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
  });
  $axios.onResponseError((err) => {
    if (err.response?.status === 401) {
      store.dispatch('auth/LOGOUT');
      return;
    }
    return Promise.reject(err);
  });

  const baseURL: string = $axios.defaults.baseURL || '';
  const arCampaignApiBaseUriPrefix: string = process.env.arCampaignApiBaseUriPrefix || '';

  $axios.sg = axios.create({
    baseURL: baseURL.replace(arCampaignApiBaseUriPrefix, '/api/v1/sg')
  });

  $axios.sg.interceptors.request.use(async config => {
    // For session-related endpoints, ensure middleware has completed
    if (config.url === '/session' || (config.url && config.url.startsWith('/session'))) {
      await ensureMiddlewareComplete();
    }

    const token = getCookie('x-auth-token') || window.sessionStorage.getItem('x-auth-token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  });

  // The first undefined means we don't do anything if it succeed, so we pass undefiend as our funtion.
  $axios.sg.interceptors.response.use(undefined, err => {
    if (err.response.status === 401) {
      store.dispatch('auth/LOGOUT');
      return;
    }
    return Promise.reject(err);
  });

  $axios.cn = axios.create({
    baseURL: baseURL.replace(arCampaignApiBaseUriPrefix, '/api/v1/cn')
  });

  $axios.cn.interceptors.request.use(async config => {
    // For session-related endpoints, ensure middleware has completed
    if (config.url === '/session' || (config.url && config.url.startsWith('/session'))) {
      await ensureMiddlewareComplete();
    }

    const token = getCookie('x-auth-token') || window.sessionStorage.getItem('x-auth-token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  });

  // The first undefined means we don't do anything if it succeed, so we pass undefiend as our funtion.
  $axios.cn.interceptors.response.use(undefined, err => {
    if (err.response.status === 401) {
      store.dispatch('auth/LOGOUT');
      return;
    }
    return Promise.reject(err);
  });

  // Used when you trying to fetch API request from 3rd-party
  $axios.mono = axios.create({});
};

export default plugin;
